// Pure helpers for resolving where a push notification should navigate.
// Shared by InAppNots (foreground snackbar), App.js (SW NOTIFICATION_CLICK
// messages) and notification-service (background tab Notification onclick).

// Resolve an absolute or relative notification URL to a same-origin relative
// path that react-router's navigate() can handle. Mirrors App.js:268-287.
export function toSameOriginPath(url) {
  if (!url) return null;
  try {
    const target = new URL(url, window.location.origin);
    if (target.origin !== window.location.origin) return null;
    return `${target.pathname}${target.search}${target.hash}`;
  } catch {
    return null;
  }
}

// Extract a ticket id + kind from a notification url so we can detect when the
// user is already viewing that ticket (gestiones: /gestion?id=<id> or
// /gestion/<id>; averias: /reporte?id=<id>).
export function parseTicketFromUrl(url) {
  if (!url) return null;
  try {
    const target = new URL(url, window.location.origin);
    if (target.origin !== window.location.origin) return null;
    const pathname = target.pathname;
    const search = target.search;
    if (pathname === '/gestion') {
      const id = new URLSearchParams(search).get('id');
      return id ? { kind: 'gestion', id: String(id) } : null;
    }
    const gestionMatch = pathname.match(/^\/gestion\/(.+)\/chat$/);
    if (gestionMatch) return { kind: 'gestion', id: String(gestionMatch[1]) };
    const gestionMatch2 = pathname.match(/^\/gestion\/(.+)$/);
    if (gestionMatch2) return { kind: 'gestion', id: String(gestionMatch2[1]) };
    if (pathname === '/reporte') {
      const id = new URLSearchParams(search).get('id');
      return id ? { kind: 'issue', id: String(id) } : null;
    }
    return null;
  } catch {
    return null;
  }
}

// Resolve where a notification click should navigate. gestion_comment pushes
// always target the comentarios thread — older pushes may carry the bare
// /gestion?id=<id> url, so derive the /chat path from the parsed ticket id
// instead of trusting the url's chat=1 param. All other kinds keep the url.
export function resolveNotificationTarget(notification) {
  const rel = toSameOriginPath(notification && notification.url);
  if (notification && notification.kind === 'gestion_comment') {
    const ticket = parseTicketFromUrl(rel);
    if (ticket && ticket.kind === 'gestion') {
      return `/gestion/${ticket.id}/chat`;
    }
  }
  return rel;
}

// kind is nested inside the SW buildNotificationData wrapper (data.data.kind),
// inside FCM's own FCM_MSG wrapper (data.FCM_MSG.data.kind), or directly on a
// raw FCM data dict (data.kind). Mirrors getNotificationKind in
// public/firebase-messaging-sw.js.
export function extractNotificationKind(notificationData) {
  if (!notificationData) return null;
  return notificationData.kind
    || (notificationData.data && notificationData.data.kind)
    || (notificationData.FCM_MSG && notificationData.FCM_MSG.data && notificationData.FCM_MSG.data.kind)
    || null;
}
